import { CSSProperties, FC, useLayoutEffect, useMemo, useState } from 'react';

import { defaultDropAnimation, DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Disclosure } from '@headlessui/react';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { SlideFadeIn } from '@components/animators/fade';
import { Rotate } from '@components/animators/Rotate';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { BlueLine, OverlayContainer, useSortingDefaults } from '@components/drag-and-drop/defaults';
import { FieldTypeKind } from '@components/form/v2';
import { IconCondition } from '@components/icons/condition';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconDropdown } from '@components/icons/dropdown';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { useFormBuilderContext } from './context';
import { SmallDragHandle } from './drag-handle';
import { getFieldConfigForKind } from './field-registry';
import { StaleConditionWarningIcon } from './stale-condition-warning';
import { ProjectedTreePlacement, StateField } from './state';
import { getProjectedPlacement } from './utils';

export const TreeView = () => {
  const {
    store: { useFlattenedList, useFormRootFields, useExpandedIds, actions, getState },
  } = useFormBuilderContext();
  const {
    resetState,
    activeId,
    overId,
    offset,
    setOverId,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortingDefaults();

  const fields = useFlattenedList();
  const expandedIds = useExpandedIds();
  const fieldIds = useMemo(() => fields.map((field) => field.id), [fields]);
  const rootFields = useFormRootFields();

  const projected = getProjectedPlacement({
    fields,
    overId,
    activeId,
    offset,
    expanded: expandedIds,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.over?.data.current?.dropInSection) {
      actions.moveFieldIntoSection(event.active.id.toString(), event.over.id.toString());
    } else {
      actions.moveFieldInTree({
        activeId: String(activeId),
        overId: String(overId),
        projected,
      });
    }

    resetState();
  };

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SortableContext items={fieldIds} strategy={strategy}>
        {rootFields.map((field) => (
          <RenderField key={field.id} field={field} projected={projected} />
        ))}
      </SortableContext>

      <PortalDragOverlay dropAnimation={defaultDropAnimation}>
        {activeId && (
          <OverlayContainer>
            <RenderField
              field={getState().fieldMap[String(activeId)]}
              overlay
              projected={projected}
            />
          </OverlayContainer>
        )}
      </PortalDragOverlay>
    </DndContext>
  );
};

type TreeItemProps = {
  field: StateField;
  overlay: boolean;
  projected: ProjectedTreePlacement;
};

const RenderField = ({
  field,
  overlay = false,
  projected,
}: {
  field: StateField;
  overlay?: boolean;
  projected: ProjectedTreePlacement;
}) => {
  if (field.kind === 'section') {
    return <TreeViewSection field={field} overlay={overlay} projected={projected} />;
  }

  if (field.kind === 'submit') {
    return <SubmitItem field={field} />;
  }

  return <TreeViewItem field={field} overlay={overlay} projected={projected} />;
};

const SubmitItem = ({ field }: { field: Extract<StateField, { kind: 'submit' }> }) => {
  const {
    store: { useIsFieldSelected, actions },
  } = useFormBuilderContext();
  const { Icon, TreeDisplay } = getFieldConfigForKind(field.kind);
  const isSelected = useIsFieldSelected(field.id);

  return (
    <ItemContainer>
      <Item
        data-overlay={false}
        data-selected={isSelected}
        onClick={(e) => {
          e.stopPropagation();
          actions.selectField(field.id);
        }}
      >
        <TreeIconGroup>
          <TreeIcon $color={colorMap[field.kind]}>
            <Icon color={iconColorMap[field.kind]} size={16} />
          </TreeIcon>
        </TreeIconGroup>
        <div data-preview>
          <TreeDisplay field={field} />
        </div>
      </Item>
    </ItemContainer>
  );
};

const TreeViewItem = ({ field, projected, overlay }: TreeItemProps) => {
  const {
    store: { useIsFieldSelected, actions },
  } = useFormBuilderContext();
  const { Icon, TreeDisplay } = getFieldConfigForKind(field.kind);
  const IconToUse = field.kind === 'input-number' && field.isDecimal ? IconDecimalFloat : Icon;
  const isSelected = useIsFieldSelected(field.id);

  const {
    attributes,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    isDragging,
    isOver,
  } = useSortable({
    id: field.id,
    disabled: field.builderProps?.canReorder === false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    ...(isDragging && {
      opacity: 0,
    }),
  };

  return (
    <ItemContainer ref={setDroppableNodeRef}>
      {isOver && <BlueLine $offset={projected?.level ?? 0} />}
      <div style={style} ref={setDraggableNodeRef} {...attributes} {...listeners}>
        <Item
          data-overlay={overlay}
          data-selected={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            actions.selectField(field.id);
          }}
        >
          <TreeIconGroup>
            <TreeIcon $color={colorMap[field.kind]}>
              <IconToUse color={iconColorMap[field.kind]} size={16} />
            </TreeIcon>
          </TreeIconGroup>
          <div data-preview>
            <TreeDisplay field={field} />
            <StaleConditionWarningIcon
              field={field}
              dataTestid={`formbuilder-tree-stale-condition-${field.id}`}
            />
            {'condition' in field && field.condition.conditions.length > 0 && (
              <IconCondition size={16} color="purple50" />
            )}
          </div>
          {field.builderProps?.canReorder !== false && (
            <div data-drag-handle="true">
              <SmallDragHandle />
            </div>
          )}
        </Item>
      </div>
    </ItemContainer>
  );
};

const TreeViewSection = ({
  field,
  overlay,
  projected,
}: {
  field: Extract<StateField, { kind: 'section' }>;
  overlay?: boolean;
  projected: ProjectedTreePlacement;
}) => {
  const { Icon, TreeDisplay } = getFieldConfigForKind('section');
  const {
    store: { useIsFieldSelected, useIsExpanded, useFieldsFromIds, actions },
  } = useFormBuilderContext();
  const isSelected = useIsFieldSelected(field.id);
  const isExpanded = useIsExpanded(field.id);
  const innerFields = useFieldsFromIds(field.fields);
  const [dropInto, setDropInto] = useState(false);

  // We want to allow the user to 'drop' into the section even if the section is closed.
  // The UI will show a dashed box around it to signify that option.
  const allowSectionDrop = !isExpanded || (isExpanded && field.fields.length === 0);

  const {
    attributes,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    isDragging,
    isOver,
    activeIndex,
    overIndex,
    over,
  } = useSortable({
    id: field.id,
    disabled: field.builderProps?.canReorder === false,
    data: {
      dropInSection: allowSectionDrop && dropInto,
    },
  });

  const showBorderedBox = isOver && dropInto && allowSectionDrop;
  const isMovingUp = activeIndex > overIndex;
  const expandedWithFields = isExpanded && field.fields.length > 0;

  let dragStyle: CSSProperties = {};

  if (transform) {
    dragStyle.transform = CSS.Transform.toString({
      ...transform,
      // animate up/down the full height unless we're trying to drop into
      y: expandedWithFields || !dropInto ? transform.y : transform.y / 2,
    });
  }

  if (isDragging) {
    dragStyle.opacity = 0;
  }

  useLayoutEffect(() => {
    if (!isOver || !over || !allowSectionDrop) return;

    const handler = (e: MouseEvent) => {
      if (isMovingUp) {
        setDropInto(e.clientY > over.rect.top + 16);
      } else {
        setDropInto(e.clientY < over.rect.top + 16);
      }
    };

    document.addEventListener('mousemove', handler);

    return () => {
      document.removeEventListener('mousemove', handler);
    };
  }, [isOver, over, isMovingUp, allowSectionDrop]);

  return (
    <Disclosure>
      <ItemContainer ref={setDroppableNodeRef}>
        {isOver && !(dropInto && allowSectionDrop) && <BlueLine $offset={projected?.level ?? 0} />}

        <ItemDragContainer
          $boxed={showBorderedBox}
          style={dragStyle}
          ref={setDraggableNodeRef}
          {...attributes}
          {...listeners}
        >
          <Item
            data-overlay={overlay}
            data-selected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              actions.selectField(field.id);
            }}
          >
            <TreeIconGroup>
              <Disclosure.Button
                as="button"
                onClick={(e) => {
                  e.stopPropagation();
                  actions.toggleExpansion(field.id);
                }}
              >
                <TreeIcon $color={colorMap.section}>
                  <Rotate isRotated={!isExpanded || isDragging || overlay} degrees={-90}>
                    <IconDropdown color="purple100" size={16} />
                  </Rotate>
                </TreeIcon>
              </Disclosure.Button>
              <TreeIcon $color={colorMap.section}>
                <Icon color="purple100" size={16} />
              </TreeIcon>
            </TreeIconGroup>
            <div data-preview>
              <TreeDisplay field={field as any} />
              <StaleConditionWarningIcon
                field={field}
                dataTestid={`formbuilder-tree-stale-condition-${field.id}`}
              />
              {field.condition.conditions.length > 0 && (
                <IconCondition size={16} color="purple50" />
              )}
            </div>
            <div data-drag-handle="true">
              <SmallDragHandle />
            </div>
          </Item>
        </ItemDragContainer>
      </ItemContainer>

      <SectionChildren isExpanded={isExpanded && !isDragging && !overlay}>
        {innerFields.map((innerField) => (
          <RenderField key={innerField.id} field={innerField} projected={projected} />
        ))}
      </SectionChildren>
    </Disclosure>
  );
};

const SectionChildren: FC<{ isExpanded: boolean }> = ({ children, isExpanded }) => {
  return (
    <Disclosure.Panel static>
      <Collapse isCollapsed={!isExpanded}>
        <SectionItems>
          <SlideFadeIn offset={-5}>
            <AnimatePresence initial={false}>{children}</AnimatePresence>
          </SlideFadeIn>
        </SectionItems>
      </Collapse>
    </Disclosure.Panel>
  );
};

export const colorMap: Record<FieldTypeKind, Color> = {
  heading: 'greenTrans10',
  subheading: 'greenTrans10',
  html: 'greenTrans10',
  video: 'greenTrans10',
  image: 'greenTrans10',
  'content-file': 'greenTrans10',

  section: 'indigoTrans10',
  separator: 'indigoTrans10',
  submit: 'indigoTrans10',

  'input-text-small': 'orangeTrans10',
  'input-text-large': 'orangeTrans10',
  'input-number': 'orangeTrans10',
  'input-date': 'orangeTrans10',
  'input-file': 'orangeTrans10',
  'input-phone': 'orangeTrans10',
  'input-email': 'orangeTrans10',

  'input-dropdown': 'blueTrans10',
  'input-multiple-choice': 'blueTrans10',
  'input-single-choice': 'blueTrans10',

  'input-kennitala': 'orangeTrans10',
};

export const iconColorMap: Record<FieldTypeKind, Color> = {
  heading: 'elementGreen200',
  subheading: 'elementGreen200',
  html: 'elementGreen200',
  video: 'elementGreen200',
  image: 'elementGreen200',
  'content-file': 'elementGreen200',

  section: 'elementIndigo100',
  separator: 'elementIndigo100',
  submit: 'elementIndigo100',

  'input-text-small': 'elementOrange200',
  'input-text-large': 'elementOrange200',
  'input-number': 'elementOrange200',
  'input-date': 'elementOrange200',
  'input-file': 'elementOrange200',
  'input-phone': 'elementOrange200',
  'input-email': 'elementOrange200',

  'input-dropdown': 'elementBlue100',
  'input-multiple-choice': 'elementBlue100',
  'input-single-choice': 'elementBlue100',

  'input-kennitala': 'elementOrange200',
};

const ItemContainer = styled.div({
  position: 'relative',
});

const SectionItems = styled.div({
  paddingLeft: pxToRem(22),
  position: 'relative',
});

const Item = styled.div({
  padding: '0 0.25rem',
  transition: transitionStandard('background'),
  display: 'grid',
  gridTemplateColumns: `max-content auto max-content`,
  width: '100%',
  gap: '0.5rem',
  borderRadius: '0.25rem',
  alignItems: 'center',
  height: pxToRem(32),
  cursor: 'pointer',
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  borderColor: 'transparent',
  font: theme.font.regular14,
  overflow: 'hidden',

  '[data-drag-handle="true"]': {
    display: 'none',
  },

  '[data-preview]': {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    minWidth: 0,

    '> span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flexGrow: 1,
    },
  },

  '&[data-selected="true"]': {
    borderColor: theme.color.uiTrans10,
  },

  '&[data-selected="false"]': {
    borderColor: 'transparent',
  },

  '&[data-dragging="true"]': {
    opacity: 0.2,
  },

  '&[data-overlay="false"]:hover': {
    background: theme.color.uiTrans5,
    gridTemplateColumns: `max-content auto max-content`,

    '[data-drag-handle="true"]': {
      display: 'block',
    },
  },
  '&[data-overlay="true"]': {
    '[data-drag-handle="true"]': {
      display: 'block',
    },
  },
});

export const TreeIconGroup = styled.div({
  borderRadius: '0.25rem',
  display: 'flex',
  justifyContent: 'flex-start',
  width: 'auto',
  alignItems: 'center',
  background: 'transparent',
  overflow: 'hidden',
  flexShrink: 0,
  button: {
    cursor: 'pointer',
    background: 'transparent',
    padding: 0,
    margin: 0,
  },
});

export const TreeIcon = styled.div<{ $color: Color }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(24),
  height: pxToRem(24),
  background: theme.color[p.$color],
}));

const ItemDragContainer = styled.div<{ $boxed: boolean }>((p) => ({
  borderWidth: pxToRem(1),
  borderStyle: 'dashed',
  borderColor: p.$boxed ? theme.color.blue100 : 'transparent',
  borderRadius: '0.25rem',
}));
